import React, { useState } from "react";
import { net } from "./database";
import { Loader2 } from "lucide-react";
import { usePrimaryColor } from "./components/content/components/PrimaryColorContext";


/**
 * Login screen — authenticates users via username/password or stored JWT token.
 *
 * Restyled to match the Flat Premium design system:
 * - Tailwind CSS (no MUI)
 * - Brand accent via CSS custom properties (--primary-color, --primary-hover)
 * - Design-system shadow, border-radius, typography, and transitions
 *
 * @param {Object} props
 * @param {string|undefined} props.apiKey - Stored JWT from localStorage (may be undefined)
 * @param {Function} props.setUser - Callback to set the authenticated user object
 * @param {Function} props.setApiKey - Callback to persist the new API key in App state
 * @returns {JSX.Element}
 *
 * @author Forge
 * @version 2.0
 * @since 2026-04-13
 */
export default function Login({ apiKey, setUser, setApiKey }) {
  const [checkKey, setCheckKey] = useState(apiKey !== undefined);
  const { primaryColor } = usePrimaryColor();
  const [error, setError] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");

  const login = () => {
    if (loggingIn) return;
    if (!username.trim() || !password.trim()) {
      setError("Please enter your email and password");
      return;
    }
    setLoggingIn(true);
    setError("");
    net
      .post("/login", { username, password })
      .then((r) => {
        if (r.error) {
          setError(r.error);
          setLoggingIn(false);
          return;
        }
        localStorage.setItem("apiKey", r.jwt);
        setApiKey(r.jwt);
        setUser(r.user);
      })
      .catch(() => {
        setError("Network error");
        setLoggingIn(false);
      });
  };

  React.useEffect(() => {
    if (!checkKey) return;
    net
      .post("/loginwithtoken", {})
      .then((r) => {
        if (r.error) {
          setError(r.error);
          setCheckKey(false);
          return;
        }
        if (r.accessToken) {
          localStorage.setItem("apiKey", r.jwt);
          setApiKey(r.accessToken);
          setUser(r.user);
          return;
        }
        localStorage.setItem("apiKey", apiKey);
        setApiKey(apiKey);
        setUser(r.user);
      })
      .catch(() => {
        setCheckKey(false);
      });
  }, [checkKey]);

  if (checkKey) {
    return (
      <div className="flex items-center justify-center h-screen bg-slate-50">
        <Loader2 size={32} className={`animate-spin text-${primaryColor}-800`} />
      </div>
    );
  }

  const inputClasses =
    `w-full h-11 px-4 rounded-[10px] border border-slate-200 bg-white text-slate-800 text-sm placeholder-slate-400 outline-none transition-[border-color,box-shadow] duration-150 ease-[cubic-bezier(0.4,0,0.2,1)] focus:border-${primaryColor}-800 focus:ring-2 focus:ring-${primaryColor}-50`;

  return (
    <div className="flex items-center justify-center min-h-screen bg-slate-50">
        <div className="w-[380px]">
          <div className="bg-white rounded-xl border border-slate-100 shadow-[0_8px_30px_rgba(0,0,0,0.08)] overflow-hidden">
          {/* Brand accent bar */}
          <div className={`h-1 bg-${primaryColor}-800`} />

          <div className="px-8 pt-8 pb-6">
            <h1 className="font-heading text-lg font-medium text-slate-800 mb-1">
              Welcome to HT Systems
            </h1>
            <p className="text-sm text-slate-400 mb-6">
              Sign in to your account
            </p>

            <div className="flex flex-col gap-4">
              <div>
                <label
                  htmlFor="login-email"
                  className="block text-xs font-medium text-slate-500 mb-1.5"
                >
                  Email
                </label>
                <input
                  id="login-email"
                  type="text"
                  autoComplete="username"
                  autoFocus
                  required
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="you@example.com"
                  className={inputClasses}
                />
              </div>

              <div>
                <label
                  htmlFor="login-password"
                  className="block text-xs font-medium text-slate-500 mb-1.5"
                >
                  Password
                </label>
                <input
                  id="login-password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") login();
                  }}
                  placeholder="Enter your password"
                  className={inputClasses}
                />
              </div>

              {error && (
                <p className="text-red-500 text-sm">{error}</p>
              )}

              <button
                type="button"
                onClick={login}
                disabled={loggingIn}
                className={`w-full h-11 rounded-[10px] bg-teal-800 hover:bg-${primaryColor}-900 active:scale-[0.98] text-white text-sm font-medium transition-all duration-150 ease-[cubic-bezier(0.4,0,0.2,1)] disabled:opacity-60 disabled:cursor-not-allowed disabled:active:scale-100 flex items-center justify-center gap-2 cursor-pointer`}
              >
                {loggingIn && <Loader2 size={16} className="animate-spin" />}
                {loggingIn ? "Signing in..." : "Sign in"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
